import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ScrollArea,
  Box,
  Divider,
  Flex,
  Loader,
  Space,
  Text,
  Stack,
  Group,
  Collapse,
  Radio,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconChevronDown, IconChevronUp } from "@tabler/icons";
import Fuse from "fuse.js";

import { NetworkType } from "apollo/graphql.generated";
import { ReactComponent as RadioCheckedIcon } from "assets/icons/radio-checked.svg";
import {
  useContractAttributesQuery,
  useCollectionAttributesQuery,
} from "~/features/gates/queries/gates.graphql.generated";

import SectionFilterResults from "../condition-modals/sections/SectionFilterResults";

export type TokenGridAttribute = { traitType: string; traitValue: string };
export default function ContractCollectionAttributeGrid(props: {
  network?: NetworkType;
  contract?: string;
  openSeaUrl?: string;
  initialAttribute?: string;
  selectedAttribute?: TokenGridAttribute;
  onSelectAttribute: (attribute: TokenGridAttribute) => void;
}) {
  const FUSE_FUZZY_MATCH_DISTANCE = 0.33;
  const {
    network,
    contract,
    openSeaUrl,
    initialAttribute,
    selectedAttribute,
    onSelectAttribute,
  } = props;
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery] = useDebouncedValue(
    searchQuery,
    DELAY_FOR_USER_TO_FINISH_TYPING
  );

  useEffect(() => {
    runFuzzySearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const [rawData, setRawData] = useState<Record<string, string[]>>({});
  const [filteredData, setFilteredData] = useState<Record<string, string[]>>(
    {}
  );

  const { data: collectionAttributes, loading: loadingCollectionAttributes } =
    useCollectionAttributesQuery({
      variables: { slug: parseSlug(openSeaUrl) },
      skip: !openSeaUrl,
    });

  const { data: contractAttributes, loading: loadingContractAttributes } =
    useContractAttributesQuery({
      variables: { network: network!, address: contract! },
      skip: !(contract && network && contract),
    });

  useEffect(() => {
    const queryData = openSeaUrl
      ? collectionAttributes?.collectionAttributes
      : contractAttributes?.contractAttributes;

    const data: Record<string, string[]> = {};
    for (const attr of queryData?.attributes ?? []) {
      data[attr.key] = attr.value;
    }

    setRawData(data);
    setFilteredData(data);
    setSearchQuery(initialAttribute ?? "");
  }, [openSeaUrl, contractAttributes, collectionAttributes]);

  const resetFilteredData = () => {
    setFilteredData(rawData);
  };

  const mapSearchResults = (
    data: Fuse.FuseResult<TokenGridAttribute>[]
  ): Record<string, string[]> => {
    const mappedData: Record<string, string[]> = {};
    data.forEach((item) => {
      const values = mappedData[item.item.traitType];
      if (values) values.push(item.item.traitValue);
      else mappedData[item.item.traitType] = [item.item.traitValue];
    });

    return mappedData;
  };

  // https://fusejs.io/demo.html
  // https://stackblitz.com/edit/typescript-fuse-search?file=index.ts
  const fuzzyIndex = useMemo(() => {
    const flattenData = [];
    for (const key of Object.keys(rawData)) {
      for (const val of rawData[key]) {
        flattenData.push({
          traitType: key,
          traitValue: val,
        });
      }
    }

    const options = {
      // isCaseSensitive: false,
      includeScore: true,
      // shouldSort: true,
      includeMatches: true,
      // findAllMatches: false,
      minMatchCharLength: 2,
      // location: 0,
      /**
       * Threshold of 0.0 => perfect exact match
       * Threshold of 1.0 => any string
       */
      threshold: FUSE_FUZZY_MATCH_DISTANCE,
      // distance: 100,
      // useExtendedSearch: false,
      ignoreLocation: true,
      // ignoreFieldNorm: false,
      /**
       * TODO: https://linear.app/highlight-xyz/issue/ENG-3938/[frontend]-test-investigate-best-weighting-for-fuzzy-search
       *
       * You can allocate a weight to keys to give them higher (or lower) values in search results. The weight value has to be greater than 0.
       *
       * I made the attribute value (the "trait"), twice as important as the "attribute type"
       */
      keys: [
        { name: "traitType", weight: 1 },
        { name: "traitValue", weight: 3 },
      ],
    };
    const myIndex = Fuse.createIndex(options.keys, flattenData);

    return new Fuse(flattenData, options, myIndex);
  }, [rawData]);

  const runFuzzySearch = async (filterString: string) => {
    // bail early
    if (!filterString) {
      resetFilteredData();
      return;
    }

    const searchResults = fuzzyIndex.search<TokenGridAttribute>(filterString);
    // we might want to put the full filter / search result here,
    // would enable us to do cool stuff like bolden / highlight the part of name that matched
    // https://stackblitz.com/edit/typescript-fuse-search?file=index.ts
    setFilteredData(mapSearchResults(searchResults));
  };

  return (
    <Box>
      <SectionFilterResults
        placeholderText="Search by trait"
        query={searchQuery || ""}
        onChangeQuery={(value) => {
          setSearchQuery(value);
        }}
        isLoading={loadingContractAttributes || loadingCollectionAttributes}
      />

      <Space h="xl" />

      <ScrollArea.Autosize mih={334} maxHeight={334}>
        <AttributeListSection
          data={filteredData}
          onSelect={onSelectAttribute}
          available={true}
          loading={loadingContractAttributes || loadingCollectionAttributes}
          selectedAttribute={selectedAttribute}
        />
      </ScrollArea.Autosize>
    </Box>
  );
}

const parseSlug = (openSeaUrl?: string) => {
  if (!openSeaUrl) {
    return "";
  }
  const parts = openSeaUrl.split("/");
  return parts[parts.length - 1];
};

const DELAY_FOR_USER_TO_FINISH_TYPING = 350; // milliseconds

type AttributeListSectionProps = {
  data: Record<string, string[]>;
  onSelect: (attribute: TokenGridAttribute) => void;
  available: boolean;
  loading: boolean;
  selectedAttribute?: TokenGridAttribute;
};

export const AttributeListSection = ({
  data,
  onSelect,
  available,
  loading,
  selectedAttribute,
}: AttributeListSectionProps) => {
  const noAttributes = !Object.entries(data).length;

  // useEffect(() => {
  //   if (!available || loading || noAttributes) {
  //     onSelect(undefined);
  //   }
  // }, [noAttributes, onSelect, available, loading]);

  if (!available) return null;
  if (loading) {
    return (
      <>
        <Flex align="center" justify="center" h={334}>
          <Loader />
        </Flex>
      </>
    );
  }

  if (noAttributes) {
    return (
      <>
        <Flex align="center" justify="center" h={334}>
          <Text size={"lg"}>No attributes found</Text>
        </Flex>
      </>
    );
  }

  return (
    <>
      <Stack spacing={0}>
        {Object.keys(data).map((attribute, index) => (
          <TokenAttributeBox
            key={`${attribute}:${index}`}
            attributeName={attribute}
            attributeValues={data[attribute]}
            onSelect={onSelect}
            selectedAttribute={selectedAttribute}
          />
        ))}
      </Stack>
    </>
  );
};

const radioStyles = {
  body: {
    display: "flex",
    justifyContent: "space-between",
  },
  label: {
    fontWeight: 400,
    opacity: 0.6,
    cursor: "pointer",
  },
  icon: {
    width: "20px",
    height: "20px",
    top: "calc(50% - 10px)",
    left: "calc(50% - 10px)",
  },
};

type TokenAttributeBoxProps = {
  attributeName: string;
  attributeValues: string[];
  onSelect: (value: TokenGridAttribute) => void;
  selectedAttribute?: TokenGridAttribute;
};

const TokenAttributeBox = ({
  attributeName,
  attributeValues,
  onSelect,
  selectedAttribute,
}: TokenAttributeBoxProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.currentTarget.checked) {
        onSelect({
          traitValue: event.currentTarget.value,
          traitType: attributeName,
        });
      }
    },
    [onSelect, attributeName]
  );
  return (
    <>
      <Group
        p={14}
        pl={0}
        pr={14}
        position="apart"
        sx={{ cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <Text size="md">{attributeName}</Text>
        {open ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
      </Group>
      <Collapse in={open}>
        <Divider mb="md" color={"gray.1"} />
        <Stack ml="lg" spacing={14} pr={14}>
          {open &&
            attributeValues.map((value, index) => (
              <React.Fragment key={`${attributeName}:${value}:${index}`}>
                <Radio
                  defaultChecked={
                    selectedAttribute?.traitType.toLowerCase() ===
                      attributeName.toLowerCase() &&
                    selectedAttribute?.traitValue.toLowerCase() ===
                      value.toLowerCase()
                  }
                  name="traits"
                  onChange={handleOnChange}
                  styles={radioStyles}
                  size="sm"
                  icon={RadioCheckedIcon}
                  labelPosition="left"
                  label={value}
                  value={value}
                />
                {index < attributeValues.length - 1 ? <Divider /> : <Space />}
              </React.Fragment>
            ))}
        </Stack>
      </Collapse>
      <Divider />
      {/* TODO: https://linear.app/highlight-xyz/issue/ENG-3972/[frontend-base-components]-different-shades-exist-for-dividers */}
    </>
  );
};
